<script>
  import { getContext, tick, onMount } from "svelte";
  import { navigate } from "svelte-routing";
  import { downloadString } from "downloaders";
  import { clickOutside } from "svelte-utilities";

  import Navbar from "#lib/Navbar.svelte";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";
  import ArchiveIcon from "@local/assets/icons/archive.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";

  import PDFExchangeJobListCard from "#lib/home/PDFExchangeJobListCard.svelte";
  import LabeledToggleButton from "#lib/LabeledToggleButton.svelte";
  import CreateModal from "#lib/home/CreateModal.svelte";
  import FilterInput from "#lib/home/FilterInput.svelte";
  import DownloadModal from "#lib/home/DownloadModal.svelte";

  import { profile } from "#src/stores/auth.js";
  import { cloneJob as cj } from "#src/stores/jobs.js";
  import { jobs, filteredJobs, jobArray, jobFilter } from "#src/stores/review-jobs.js";
  import { createGroup, fetchGroup } from "#src/stores/group.js";
  import eb from "#src/extensions/event-bus.js";
  import { myRfqsOnly } from "#src/stores/ui.js";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";
  import { exportJson } from "#src/extensions/export-job/json.js";
  import { api } from "#src/api";

  const org = getContext("org");

  let groupData;
  let creationInProgress = false;
  let deletingJobs = {};
  let createButton = "tally";
  let showButtonMenu = false;

  $: jobArr = jobArray(filteredJobs);
  $: mineAllValue = $myRfqsOnly ? "left" : "right";
  $: group = groupData && createGroup(groupData, { realtime: true });
  $: types = transform($group?.types, orderedList);

  function transform(coll, xf, defaultValue = []) {
    return coll ? xf(coll) : defaultValue;
  }

  async function fetchJobs() {
    await jobs.fetch();
    jobs.subscribeToDbUpdates();
  }

  function toggleShowMine(e) {
    const mine = e.detail.value;
    if (mine === "left") {
      $myRfqsOnly = true;
    } else {
      $myRfqsOnly = false;
    }
  }

  async function archiveJob(e) {
    const { id } = e.detail;
    await jobs.archive(id);
  }

  async function cloneJob(e) {
    const { id } = e.detail;
    const assignedTo = $profile?.profile_capabilities?.capabilities?.switch_organization
      ? $profile?.id
      : null;
    const job = $jobs[id];
    const newJobId = await cj(job.current_group_id, assignedTo);
    jobs.fetchOne(newJobId);
  }

  function deleteJob(e) {
    const { id } = e.detail;
    deletingJobs[id] = true;
    jobs.remove(id);
  }

  async function exportFile(e) {
    const { id } = e.detail;
    const job = $jobs[id];
    groupData = await fetchGroup(job.current_group_id);
    await tick();

    const json = exportJson($group, job);
    downloadString(json, `${job.name}.json`);
  }

  async function updateJob(e) {
    const { id, update } = e.detail;
    await api.from("jobs").update(update).eq("id", id).select("*").single();
    jobs.fetchOne(id);
  }

  async function downloadJob(e) {
    const { id } = e.detail;
    const job = $jobs[id];
    groupData = await fetchGroup(job.current_group_id);
    await tick();
    eb.dispatch("open-download");
  }

  onMount(() => {
    $myRfqsOnly = false;
  });
</script>

<Navbar context="reviewer-job-list" />
{#if $profile && $org}
  <div class="grow bg-gray-100 home-container">
    {#await fetchJobs()}
      <div class="text-gray-600 p-4 h-24 w-24 relative">
        <div class="absolute">
          <div class="animate-spin">
            <SpinnerIcon />
          </div>
        </div>
      </div>
    {:then}
      <div class="list-container">
        <div class="h-full w-full flex flex-col items-center overflow-y-auto">
          <div class="content-container p-4 space-y-4">
            <div class="flex justify-end items-center px-4">
              <div class="flex gap-2 items-center">
                <FilterInput bind:filter={$jobFilter} org={$org} />
                <button
                  class="border rounded-md h-8 border-gray-400 px-2 hover:border-gray-600"
                  on:click={() => navigate(`/archive`)}>
                  <ArchiveIcon />
                </button>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {#if creationInProgress}
                <div
                  class="border border-dashed border-gray-400 rounded-md bg-white opacity-50 p-4 flex gap-4 items-center">
                  <div class="text-gray-800 h-8 w-8 relative">
                    <div class="absolute">
                      <div class="animate-spin">
                        <SpinnerIcon />
                      </div>
                    </div>
                  </div>
                  <div class="text-gray-800 italic text-sm">Creating job...</div>
                </div>
              {:else}
                <button
                  class="border border-dashed border-gray-400 rounded-md hover:bg-gray-200 p-4 text-sm"
                  on:click={() => eb.dispatch("create-rfq")}>
                  + New Job
                </button>
              {/if}
              {#each $jobArr as job}
                <PDFExchangeJobListCard
                  {job}
                  deleting={deletingJobs[job?.id]}
                  on:download-job={downloadJob}
                  on:update-job={updateJob}
                  on:archive-job={archiveJob}
                  on:clone-job={cloneJob}
                  on:delete-job={deleteJob}
                  on:export-json={exportFile} />
              {/each}
            </div>
          </div>
        </div>
      </div>

      <DownloadModal title="Download Contents" {group} {types} name={$group?.job.name} />
    {/await}
  </div>

  <CreateModal
    isReviewer
    org={$org}
    on:begin-creating={() => (creationInProgress = true)}
    on:end-creation={() => (creationInProgress = false)} />
{/if}

<style lang="scss">
  .home-container {
    display: block;
    position: relative;
    overflow-y: hidden;

    .list-container {
      width: 100%;
      height: 100%;
    }

    .content-container {
      width: 100%;

      @media (min-width: 768px) {
        width: 52.5rem;
      }
    }
  }
</style>
